import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Settings, X, Save } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface BacklinkSettingsProps {
  user: { id: string };
  website: { id: string; domain: string };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSettingsUpdated?: () => void;
}

interface BacklinkSettingsData {
  keywords: string[];
  preferred_service_id?: string;
  preferred_service_name?: string;
}

export function BacklinkSettings({ user, website, open: controlledOpen, onOpenChange, onSettingsUpdated }: BacklinkSettingsProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<BacklinkSettingsData>({
    keywords: ['', '', ''],
    preferred_service_id: undefined,
    preferred_service_name: undefined,
  });

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open, user.id, website.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('backlink_settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('website_id', website.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        // Ensure we have 3 keywords (pad with empty strings if needed)
        const keywords = Array.isArray(data.keywords) ? data.keywords : [];
        const paddedKeywords = [...keywords, '', '', ''].slice(0, 3);
        
        setSettings({
          keywords: paddedKeywords,
          preferred_service_id: data.preferred_service_id || undefined,
          preferred_service_name: data.preferred_service_name || undefined,
        });
      } else {
        // No settings found, use defaults
        setSettings({
          keywords: ['', '', ''],
          preferred_service_id: undefined,
          preferred_service_name: undefined,
        });
      }
    } catch (error: any) {
      console.error('Error loading backlink settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Filter out empty keywords
      const validKeywords = settings.keywords.filter(k => k.trim().length > 0);
      
      if (validKeywords.length === 0) {
        toast.error('Please enter at least one keyword');
        return;
      }

      const { error } = await supabase
        .from('backlink_settings')
        .upsert({
          user_id: user.id,
          website_id: website.id,
          keywords: validKeywords,
          preferred_service_id: settings.preferred_service_id || null,
          preferred_service_name: settings.preferred_service_name || null,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,website_id',
        });

      if (error) throw error;

      toast.success('Settings saved successfully!');
      onSettingsUpdated?.();
      setOpen(false);
    } catch (error: any) {
      console.error('Error saving backlink settings:', error);
      toast.error(`Failed to save settings: ${error?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const updateKeyword = (index: number, value: string) => {
    const newKeywords = [...settings.keywords];
    newKeywords[index] = value;
    setSettings({ ...settings, keywords: newKeywords });
  };

  return (
    <>
      {controlledOpen === undefined && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="rounded-full border-2"
        >
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Backlink Settings
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Configure your backlink preferences for {website.domain}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Keywords</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter up to 3 keywords that will be used for your backlinks. These keywords help determine where and how your backlinks are placed.
                </p>
                {[0, 1, 2].map((index) => (
                  <div key={index} className="space-y-2">
                    <Label htmlFor={`keyword-${index}`}>
                      Keyword {index + 1} {index === 0 && <span className="text-muted-foreground">(required)</span>}
                    </Label>
                    <Input
                      id={`keyword-${index}`}
                      value={settings.keywords[index] || ''}
                      onChange={(e) => updateKeyword(index, e.target.value)}
                      placeholder={`Enter keyword ${index + 1}`}
                      className="rounded-xl"
                      disabled={loading}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-full"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={saveSettings}
                className="rounded-full"
                disabled={saving || loading}
              >
                {saving ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-background border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

